'use client';

import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { CommunityEvent } from '@/types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  generateGoogleCalendarUrl,
  generateOutlookUrl,
  generateYahooUrl,
  downloadIcsFile,
} from '@/lib/calendar-utils';
import { Calendar, CheckCircle2, Mail, Loader2, Sparkles } from 'lucide-react';
import { sendGAEvent } from '@next/third-parties/google';
import { usePostHog } from 'posthog-js/react';
import { clientLogger } from '@/lib/logger-client';

interface EventRegistrationModalProps {
  event: CommunityEvent;
  isOpen: boolean;
  onClose: () => void;
  onRegisteredSuccess?: () => void;
}

export default function EventRegistrationModal({
  event,
  isOpen,
  onClose,
  onRegisteredSuccess,
}: EventRegistrationModalProps) {
  const { data: session } = useSession();
  const posthog = usePostHog();

  const sessionName = session?.user?.name || '';
  const sessionEmail = session?.user?.email || '';

  const [customName, setCustomName] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const name = customName || sessionName;
  const email = customEmail || sessionEmail;


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);

    try {
      const response = await fetch('/api/events/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId: event.id,
          name: name.trim(),
          email: email.trim(),
          userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to register for event');
      }

      setRegistered(true);
      if (onRegisteredSuccess) {
        onRegisteredSuccess();
      }

      // Trigger immediate notification sync to clear invite from notification drawer
      if (session?.user?.id) {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
        fetch('/api/notifications/sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userTimezone }),
        }).catch(() => {});
      }

      // Track analytics
      try {
        sendGAEvent('event', 'event_registered', {
          event_id: event.id,
          event_title: event.title,
          is_authenticated: !!session?.user?.id,
        });

        if (posthog) {
          posthog.capture('event_registered', {
            event_id: event.id,
            event_title: event.title,
            is_authenticated: !!session?.user?.id,
          });
        }
      } catch (err) {
        clientLogger.warn('[RegistrationModal] Analytics tracking failed', { err });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An unexpected error occurred';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const calendarEvent = {
    id: event.id,
    title: event.title,
    description: event.description,
    startDate: event.eventDate,
    endDate: new Date(event.eventDate.getTime() + event.durationMinutes * 60000),
    location: event.meetingLink,
    url: event.meetingLink,
  };

  const handleCalendarAdd = (platform: 'google' | 'outlook' | 'yahoo' | 'ics') => {
    switch (platform) {
      case 'google':
        window.open(generateGoogleCalendarUrl(calendarEvent), '_blank');
        break;
      case 'outlook':
        window.open(generateOutlookUrl(calendarEvent), '_blank');
        break;
      case 'yahoo':
        window.open(generateYahooUrl(calendarEvent), '_blank');
        break;
      case 'ics':
        downloadIcsFile(calendarEvent);
        break;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md rounded-2xl p-6 border-slate-200 dark:border-slate-800 shadow-xl">
        {!registered ? (
          <>
            <DialogHeader className="text-left space-y-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 text-xs font-semibold w-fit">
                <Sparkles className="w-3.5 h-3.5" />
                Free Community Workshop
              </div>
              <DialogTitle className="text-xl font-bold text-slate-900 dark:text-slate-100">
                Register for {event.title}
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-600 dark:text-slate-400">
                Reserve your spot in one click. We will email you the calendar invite and Zoom link. <br /><br /><strong> Use the email address associated with your calendar.</strong>
                <br /><br /><strong> Time changes and cancellation alerts will be sent to this email.</strong>
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 pt-2">
              {errorMessage && (
                <div className="p-3 text-xs rounded-lg bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 border border-red-200 dark:border-red-800">
                  {errorMessage}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="reg-name" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Full Name
                </Label>
                <Input
                  id="reg-name"
                  type="text"
                  placeholder="Jane Doe"
                  value={name}
                  onChange={(e) => setCustomName(e.target.value)}
                  required
                  className="rounded-lg text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reg-email" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Email Address
                </Label>
                <Input
                  id="reg-email"
                  type="email"
                  placeholder="jane@example.com"
                  value={email}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  required
                  className="rounded-lg text-sm"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !name.trim() || !email.trim()}
                className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 shadow-xs"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  'Complete Registration'
                )}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-4 space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-100 dark:bg-green-950/50 flex items-center justify-center text-green-600 dark:text-green-400">
              <CheckCircle2 className="w-6 h-6" />
            </div>

            <DialogHeader className="text-center space-y-1">
              <DialogTitle className="text-xl font-bold text-slate-900 dark:text-slate-100">
                You&apos;re Registered! 🎉
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-600 dark:text-slate-400">
                We dispatched an email confirmation to <span className="font-semibold text-slate-800 dark:text-slate-200">{email}</span>. Click the button in the email to confirm your RSVP!
              </DialogDescription>
            </DialogHeader>

            <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
              <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center justify-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-blue-600" />
                Add directly to your calendar now:
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCalendarAdd('google')}
                  className="text-xs rounded-lg"
                >
                  Google Calendar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCalendarAdd('outlook')}
                  className="text-xs rounded-lg"
                >
                  Outlook
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCalendarAdd('yahoo')}
                  className="text-xs rounded-lg"
                >
                  Yahoo Calendar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCalendarAdd('ics')}
                  className="text-xs rounded-lg"
                >
                  Download .ICS
                </Button>
              </div>
            </div>

            <Button
              onClick={onClose}
              variant="default"
              className="w-full mt-2 rounded-xl bg-slate-900 dark:bg-slate-100 dark:text-slate-900 text-white hover:opacity-90"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog >
  );
}
